// src/components/SentimentListView.js
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import DetailedListView from './DetailedListView'; // Assuming correct path
import { sentimentQueries } from '../repository/graphQueries'; // Assuming path
import { formatDate, formatDateTime } from '../utils';
import CallEvalDisplay from './CallEvalDisplay'; // Adjust path if needed

// --- Columns (Copied from original Dashboard.js) ---
const communicationColumns = [
    { header: 'Date', key: 'date', formatter: formatDateTime},
    { header: 'Type', key: 'type'},
    { header: 'Title', key: 'title'},
    { header: 'Staff', key: 'staff'},
        // --- UPDATED Call Scores Column ---
    {
        header: 'Call Scores',
        key: 'callEvaluations',
        width: '50%', // Assign more width potentially
        // Use the component directly as the formatter/renderer
        formatter: (evals) => (evals && evals.length > 0) ? <CallEvalDisplay evaluations={evals} /> : <span className="text-gray-400 italic text-xs">No Evals</span>
    },
    { header: 'Summary', key: 'summary', type: 'longText'},
    // { header: 'Participants', key: 'participants'},
    // { header: 'Entities Mentioned', key: 'evidencedEntities', formatter: (val) => val?.map(e => e.name).join(', ') }, // Example formatter
];

// --- Component ---
const SentimentListView = () => {
    const { neo4jService, clientId } = useOutletContext();

    // State for Communications List
    const [allCommunicationsList, setAllCommunicationsList] = useState([]);
    const [loadingAllComms, setLoadingAllComms] = useState(false);
    const [errorAllComms, setErrorAllComms] = useState(null);

    // State for Sentiment Chart
    const [sentimentData, setSentimentData] = useState([]);
    const [loadingSentimentChart, setLoadingSentimentChart] = useState(false);

    // Load Communications List (adapted from original Dashboard.js)
    const loadAllCommunications = useCallback(async () => {
        if (!neo4jService || !clientId) return;
        setLoadingAllComms(true); setErrorAllComms(null);
        console.log(`SentimentListView loading Communications for client: ${clientId}`);
        try {
            const comms = await neo4jService.runQuery(sentimentQueries.getAllCommunicationsList, { clientId });
            setAllCommunicationsList(comms);
        } catch (e) { setErrorAllComms(e.message); setAllCommunicationsList([]); }
        finally { setLoadingAllComms(false); }
    }, [neo4jService, clientId]);

     // Load Sentiment Chart Data
      const loadSentimentChartData = useCallback(async () => {
          if (!neo4jService || !clientId) return;
          setLoadingSentimentChart(true);
          try {
              const sentimentResult = await neo4jService.runQuery(sentimentQueries.getSentimentOverTime, { clientId });
              setSentimentData(sentimentResult?.map(item => ({
                    ...item,
                    day: formatDate(item.day) // Ensure date is formatted for XAxis
              })) ?? []);
          } catch (err) { console.error("Error loading sentiment chart data:", err); setSentimentData([]); }
          finally { setLoadingSentimentChart(false); }
      }, [neo4jService, clientId]);

    // useEffect to load data when component mounts or clientId changes
    useEffect(() => {
        loadAllCommunications();
        loadSentimentChartData();
    }, [loadAllCommunications, loadSentimentChartData]); // Depend on the load functions


    return (
        <>
            <h2 className="text-xl font-semibold mb-6">Communications & Sentiment</h2>

            {/* Chart */}
            <div className="bg-white rounded-lg shadow p-4 md:p-6 mb-6 md:mb-8">
                 <h3 className="text-lg font-medium text-gray-900 mb-4">Sentiment Trend</h3>
                 {loadingSentimentChart ? <p>Loading chart...</p> : sentimentData.length > 0 ? (
                     <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={sentimentData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="day" tick={{fontSize: 10}} />
                            <YAxis domain={[0, 1]} tickFormatter={(value) => `${(value * 100).toFixed(0)}%`} tick={{fontSize: 10}} />
                            <Tooltip wrapperClassName="text-xs" formatter={(value) => [`${(value * 100).toFixed(1)}%`, 'Sentiment']} labelFormatter={(value) => `Date: ${value}`} />
                            <Line type="monotone" dataKey="avgSentiment" stroke="#3B82F6" strokeWidth={2} dot={{r: 3}} activeDot={{r: 5}} name="Avg Sentiment" />
                        </LineChart>
                      </ResponsiveContainer>
                  ) : (
                      <p className="text-center text-sm text-gray-500 py-4">No sentiment data available.</p>
                  )}
             </div>

            {/* List */}
            <DetailedListView
                title="All Communications" // Updated title
                items={allCommunicationsList}
                columns={communicationColumns}
                isLoading={loadingAllComms}
                error={errorAllComms}
                // Optional: Add entityType='communications' if you create a detail page for comms
                // entityType="communications"
            />
        </>
    );
};

export default SentimentListView;